import * as React from 'react';
import * as _ from 'lodash';
import {
  API,
  capitalizeFirstLetterEachWord,
  CertificateStatus,
  getUuidV4,
  Logging,
  SelectorButton,
  SelectorButtonOption,
} from '@vcd-ecm-ui/Common';
import {
  GenericGrid,
  ActionMenu,
  ColumnLink,
  FilterModelApi,
  GridDataSummaryCollection,
  SortModelApi,
  Action,
  ActionDivider,
  mergeDefaultAndCustomColumns,
  getSelectFilterAsString,
  FilterTypeEnum,
  GridFilter,
  GridSort,
  SelectFilterType,
  getSelectFilterFromFilters,
  BulkAction,
  BulkActionsInfo,
} from '@vcd-ecm-ui/GenericGrid';
import { VtxButton, VtxColumnProps, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import CertificateReportsProps from './CertificateReportsProps.interface';
import GridRowData, { GridDataNativeType } from './GridRowData';
import getDefaultColumns, { additionalTableSettings } from './defaultColumns';
import '../common-reports.scss';
import './certificate-reports.scss';
import useJurisdictions from '../Common/useJurisdictions';

const CertificateReports = ({
  gatewayEndPoint,
}: CertificateReportsProps): JSX.Element => {
  // Get the Status Filter
  const getStatusFilter = (status: string): GridFilter => {
    const statusFilter: GridFilter = {
      column: 'status',
      value: status === CertificateStatus.All
        ? null
        : getSelectFilterAsString(FilterTypeEnum.Text, null, false, [status], [capitalizeFirstLetterEachWord(status)]),
    };

    return statusFilter;
  };

  const [jurisdictions, jurisdictionNotification] = useJurisdictions(gatewayEndPoint);

  // Selected Status
  const [selectedStatus, setSelectedStatus] = React.useState(sessionStorage.certificateStatus
    ? sessionStorage.certificateStatus
    : CertificateStatus.Pending);

  // Create teh External Status Filter
  const [externalFilter, setExternalFilter] = React.useState(getStatusFilter(selectedStatus));

  // Parse each row from the Native Data Type to the Actual Data Type
  const rowParser = (row: GridDataNativeType): GridRowData => GridRowData.fromData(row);

  const onCertificateIdClick = (row: GridRowData): void => {
    Logging.Info('Certificate Id Clicked:', row.rowData);
  };

  // Generic Action Menu Handler
  const onActionMenuClick = (actionKey: string, row: GridRowData): void => {
    Logging.Info('Action Menu Clicked:', actionKey, row.rowData);
  };

  // Get the Action Menu Actions for the Row
  const getActionMenuActions = (row: GridRowData): (Action | ActionDivider)[] => {
    const actions: (Action | ActionDivider)[] = [
      {
        key: 'add',
        name: 'Add',
        onClick: onActionMenuClick,
        row,
      },
      new ActionDivider(),
      {
        key: 'edit',
        name: 'Edit',
        onClick: onActionMenuClick,
        disabled: true,
        row,
      },
      new ActionDivider(),
      {
        key: 'delete',
        name: 'Delete',
        onClick: onActionMenuClick,
        row,
      },
    ];

    return actions;
  };

  // Get the Columns for the Grid
  const getColumns = (): VtxColumnProps<GridRowData>[] => {
    // Get the Default Columns
    const defaultColumns: VtxColumnProps<GridRowData>[] = getDefaultColumns();

    // Column Columns with Dynamic Renderer
    const customColumns = [
      {
        dataIndex: 'jurisdiction',
        filters: jurisdictions?.map((jurisdiction) => { // eslint-disable-line arrow-body-style
          return {
            text: jurisdiction.name,
            value: jurisdiction.abbreviation,
          };
        }),
      },
      {
        dataIndex: 'action',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => <ActionMenu actions={getActionMenuActions(row)} />,
      },
      {
        dataIndex: 'certificateId',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => (
          <ColumnLink
            id="certificateIdLink"
            icon="paper-clip"
            iconPlacement="right"
            linkText={value}
            onClick={onCertificateIdClick}
            row={row}
          />
        ),
      },
    ];

    // Merge the Custom Columns into the Default Columns
    return mergeDefaultAndCustomColumns(defaultColumns, customColumns);
  };

  // The API Call used to retrieve data for the Grid
  const apiGetRowsServerModel = (
    startRow:number,
    endRow:number,
    filterModel: FilterModelApi,
    sortModel: SortModelApi,
  ): Promise<GridDataSummaryCollection> => {
    const api = new API(gatewayEndPoint);

    return api.certificate.getCertificatesV41(startRow, endRow, filterModel, sortModel);

    /*
    const promise = new Promise<ErrorLogCollection>((resolve, reject) => {
      const data: ErrorLogCollection = {
        '@odata.count': 2,
        value: [
          {
            id: 1,
            message: 'Failed to process ECW Certificate uuid=5d40349a-83d6-47de-b4c9-ae2d74e68b2f syncId=60316',
            createDateTimeStamp: '2020-04-17T16:47Z',
            lastUpdateDateTimeStamp: '2020-04-17T16:47Z',
            referenceUuid: '5096e0fc-2f66-40a7-a3ea-647eabe4179a',
            referenceType: 'Customer',
          },
          {
            id: 2,
            message: 'Failed to process ECW Certificate uuid=5d40349a-83d6-47de-b4c9-ae2d74e68b2f syncId=60316',
            createDateTimeStamp: '2020-04-17T16:47Z',
            lastUpdateDateTimeStamp: '2020-04-17T16:47Z',
            referenceUuid: 'f7dbf17a-08da-4703-bd38-7427b32ae42b',
            referenceType: 'Certificate',
          },
          {
            id: 3,
            message: 'Failed to process ECW Certificate uuid=5d40349a-83d6-47de-b4c9-ae2d74e68b2f syncId=60316',
            createDateTimeStamp: '2020-04-17T16:47Z',
            lastUpdateDateTimeStamp: '2020-04-17T16:47Z',
            referenceUuid: null,
            referenceType: 'Unknown',
          },
        ],
      };

      resolve(data);
    });

    return promise;
      */
  };

  // Status Selection Changed
  const onSelectStatus = (status: string): void => {
    setSelectedStatus(status);

    // Save the Status to the Session Storage
    sessionStorage.certificateStatus = status;

    // Update the External Filter
    setExternalFilter(getStatusFilter(status));
  };

  // Filtering or Sorting in the Grid Changed
  const onFilterSortingChange = (filters: GridFilter[], sorters: GridSort[]): void => {
    Logging.Debug('onFilterSortingChange', filters, sorters);

    // Get the Status Filter
    const selectFilter: SelectFilterType = getSelectFilterFromFilters(filters, 'status');
    const status = selectFilter ? selectFilter.values[0] : CertificateStatus.All;

    // Update the Status Button
    setSelectedStatus(status);

    // Save the Status to the Session Storage
    sessionStorage.certificateStatus = status;

    // Update the External Filter
    setExternalFilter(getStatusFilter(status));
  };

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Certificate Reports: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
    };
  }, []);

  const certStatusOptions: SelectorButtonOption[] = [
    {
      label: CertificateStatus.Pending,
      value: CertificateStatus.Pending,
      bulletClass: 'bullet-pending',
      statusClass: 'status-pending',
      userSettable: true,
    },
    {
      label: CertificateStatus.Approved,
      value: CertificateStatus.Approved,
      bulletClass: 'bullet-approved',
      statusClass: 'status-approved',
      userSettable: true,
    },
    {
      label: CertificateStatus.Rejected,
      value: CertificateStatus.Rejected,
      bulletClass: 'bullet-rejected',
      statusClass: 'status-rejected',
      userSettable: true,
    },
    {
      label: CertificateStatus.Draft,
      value: CertificateStatus.Draft,
      bulletClass: 'bullet-draft',
      statusClass: 'status-draft',
      userSettable: true,
    },
    {
      label: CertificateStatus.All,
      value: CertificateStatus.All,
      bulletClass: 'bullet-all',
      statusClass: 'status-all',
      userSettable: true,
    },
  ];

  // Bulk Action Handler
  const onApproveSelected = (selectedRowKeys: string[], selectedRows: GridRowData[], clearSelection: () => void): void => {
    Logging.Info('Approve Selected', selectedRowKeys, selectedRows);

    // Clear the Selection in the Grid when done - FIX ME
    clearSelection();
  };

  // Bulk Actions
  const bulkActions: BulkActionsInfo = {
    title: 'Bulk Actions',
    type: 'default',
    actions: [
      {
        id: 'approveSelected',
        name: 'Approve Selected',
        disabled: false,
        onClick: onApproveSelected,
      },
    ],
  };

  // Check if Helper Data is Loaded before Showing the Grid
  const dataLoaded = jurisdictions;

  return (
    <>
      <div className="reportsContainer">
        <div className="reportsHeader">
          <div className="reportsHeaderLeft">
            Certificate Reports
            <SelectorButton
              name="CertificateStatus"
              onSelect={onSelectStatus}
              options={certStatusOptions}
              selected={selectedStatus}
            />
          </div>
        </div>
        {/* Change the Key in the Parent Div to Reload */}
        <div className="reportsContentGrid">
          {
            dataLoaded && (
              <GenericGrid<GridRowData>
                id="certificateGrid"
                dataTypeDescription="Certificates"
                getDefaultColumns={getColumns}
                rowParser={rowParser}
                apiGetRowsServerModel={apiGetRowsServerModel}
                verticalHeightOffset={339} // This will handle double header line columns. Single: 30px, Double: 50px
                model="server"
                additionalTableSettings={additionalTableSettings}
                onFilterSortingChange={onFilterSortingChange}
                externalFilter={externalFilter}
                alwaysHiddenFilterBreadcrumbs={['status']}
                bulkActions={bulkActions}
                showTableManagement
                showAllFavoriteColumns
                showClearFilters
                showClearSorters
              />
            )
          }
        </div>
      </div>
    </>
  );
};

export default CertificateReports;
